import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDefinitions } from "../../hooks/useDefinitions";
import { processDefinitions } from "../../services/review.service";
import { useDictation } from "../../hooks/useDictation";
import { Button } from "./ui/button";
import { Loader } from "lucide-react";
import {
  FormActions,
  FormInputFields,
  OptionsCheckboxList,
  computeOptionChange,
  computeSelectAll,
  getSelectAllLabel,
} from "./GenerateReviewForm.utils";
import { generateReviewSchema, type GenerateReviewFormData } from "@/schemas/review.schemas";
import KnowledgeBankDialog from "./KnowledgeBankDialog";

export type { GenerateReviewFormData };

export interface GenerateReviewFormProps {
  onOptionSelected: (keys: string[]) => void;
  onCancel: () => void;
  onGenerate: (data: GenerateReviewFormData) => void;
  isGenerating: boolean;
  initialSelectedOptions: Set<string>;
}

const GenerateReviewFormView: React.FC<{
  processedOptions: { key: string; text: string }[];
  selectedOptionsSet: Set<string>;
  selectedOptions: string[];
  errors: ReturnType<typeof useForm<GenerateReviewFormData>>["formState"]["errors"];
  control: ReturnType<typeof useForm<GenerateReviewFormData>>["control"];
  isGenerating: boolean;
  handleSubmit: ReturnType<typeof useForm<GenerateReviewFormData>>["handleSubmit"];
  handleOptionChange: (key: string, checked: boolean) => void;
  handleSelectAll: () => void;
  onGenerate: (data: GenerateReviewFormData) => void;
  onCancel: () => void;
  dictation: {
    isRecording: boolean;
    isMicroLoading: boolean;
    isConverting: boolean;
    startDictation: () => void;
  };
  onKnowledgeBankClick: () => void;
}> = ({
  processedOptions,
  selectedOptionsSet,
  selectedOptions,
  errors,
  control,
  isGenerating,
  handleSubmit,
  handleOptionChange,
  handleSelectAll,
  onGenerate,
  onCancel,
  dictation,
  onKnowledgeBankClick,
}) => (
  <form
    onSubmit={handleSubmit(
      (data) => {
        onGenerate(data);
        onCancel();
      },
      () => {}
    )}
    className="flex flex-col gap-4"
  >
    <div className="flex items-center justify-between">
      <h3 className="font-medium text-sm">Review Options</h3>
      <Button
        type="button"
        variant={"link"}
        className="cursor-pointer text-sm font-medium size-auto p-0 text-primary-300"
        onClick={handleSelectAll}
      >
        {getSelectAllLabel(processedOptions, selectedOptionsSet)}
      </Button>
    </div>
    <OptionsCheckboxList
      processedOptions={processedOptions}
      selectedOptionsSet={selectedOptionsSet}
      isGenerating={isGenerating}
      onOptionChange={handleOptionChange}
    />
    {errors.selectedOptions != null && (
      <p className="text-danger-200 text-xs">{errors.selectedOptions.message}</p>
    )}
    <FormInputFields
      control={control}
      isGenerating={isGenerating}
      dictation={dictation}
      onKnowledgeBankClick={onKnowledgeBankClick}
    />
    <FormActions
      hasSelectedOptions={selectedOptions.length > 0}
      isGenerating={isGenerating}
      onCancel={onCancel}
    />
  </form>
);

const ReviewFormLoadingState: React.FC = () => (
  <div className="flex flex-col gap-3">
    <Loader className="animate-spin" />
    <p>Loading review options...</p>
  </div>
);

function useReviewFormState(initialSelectedOptions: Set<string>) {
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<GenerateReviewFormData>({
    resolver: zodResolver(generateReviewSchema),
    mode: "onBlur",
    defaultValues: {
      selectedOptions: Array.from(initialSelectedOptions),
      representingInput: "",
      considerationInput: "",
    },
  });

  // setValue is intentionally omitted: React Hook Form guarantees a stable
  // setValue reference across renders and adding it would cause unnecessary cycles.
  React.useEffect(() => {
    setValue("selectedOptions", Array.from(initialSelectedOptions));
  }, [initialSelectedOptions]);

  const selectedOptions = watch("selectedOptions");
  const considerationInput = watch("considerationInput") ?? "";
  return { control, handleSubmit, errors, setValue, selectedOptions, considerationInput };
}

function useConsiderationDictation(
  considerationInput: string,
  setValue: (name: "considerationInput", value: string) => void
) {
  const considerationRef = React.useRef(considerationInput);
  considerationRef.current = considerationInput;

  const considerationSetter = React.useCallback(
    (updater: React.SetStateAction<string>) => {
      const newVal = typeof updater === "function" ? updater(considerationRef.current) : updater;
      setValue("considerationInput", newVal);
    },
    [setValue]
  );
  const dictation = useDictation({ onTranscript: considerationSetter });
  const [knowledgeBankOpen, setKnowledgeBankOpen] = React.useState(false);

  const handleKnowledgeBankSelect = React.useCallback(
    (text: string) => {
      const current = considerationRef.current;
      const separator = current.trim() !== "" ? " " : "";
      setValue("considerationInput", current + separator + text);
    },
    [setValue]
  );

  const handleInsertDocument = React.useCallback((text: string) => {
    void Word.run(async (context) => {
      const selection = context.document.getSelection();
      selection.insertText(text, Word.InsertLocation.replace);
      await context.sync();
    });
  }, []);

  return {
    dictation,
    knowledgeBankOpen,
    setKnowledgeBankOpen,
    handleKnowledgeBankSelect,
    handleInsertDocument,
  };
}

export const GenerateReviewForm: React.FC<GenerateReviewFormProps> = ({
  onOptionSelected,
  onCancel,
  onGenerate,
  isGenerating,
  initialSelectedOptions,
}) => {
  const { data: definitions, isLoading: isLoadingDefinitions } = useDefinitions();
  const processedOptions = React.useMemo(() => {
    if (!definitions) return [];
    return processDefinitions(definitions).sort((a, b) => a.text.localeCompare(b.text));
  }, [definitions]);

  const { control, handleSubmit, errors, setValue, selectedOptions, considerationInput } =
    useReviewFormState(initialSelectedOptions);
  const selectedOptionsSet = new Set(selectedOptions);
  const {
    dictation,
    knowledgeBankOpen,
    setKnowledgeBankOpen,
    handleKnowledgeBankSelect,
    handleInsertDocument,
  } = useConsiderationDictation(considerationInput, setValue);

  const handleOptionChange = (key: string, checked: boolean) => {
    const newSelectedArray = computeOptionChange(selectedOptions, key, checked);
    setValue("selectedOptions", newSelectedArray);
    onOptionSelected(newSelectedArray);
  };

  const handleSelectAll = () => {
    const newSelectedArray = computeSelectAll(processedOptions, selectedOptionsSet);
    setValue("selectedOptions", newSelectedArray);
    onOptionSelected(newSelectedArray);
  };

  if (isLoadingDefinitions) return <ReviewFormLoadingState />;

  return (
    <>
      <GenerateReviewFormView
        processedOptions={processedOptions}
        selectedOptionsSet={selectedOptionsSet}
        selectedOptions={selectedOptions}
        errors={errors}
        control={control}
        isGenerating={isGenerating}
        handleSubmit={handleSubmit}
        handleOptionChange={handleOptionChange}
        handleSelectAll={handleSelectAll}
        onGenerate={onGenerate}
        onCancel={onCancel}
        dictation={dictation}
        onKnowledgeBankClick={() => setKnowledgeBankOpen(true)}
      />
      <KnowledgeBankDialog
        open={knowledgeBankOpen}
        onOpenChange={setKnowledgeBankOpen}
        onInsertTextarea={handleKnowledgeBankSelect}
        onInsertDocument={handleInsertDocument}
      />
    </>
  );
};

export default GenerateReviewForm;
